import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMissingSameDayProviderVisitsCount, useMissingSameDayProviderVisitsData } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Table } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../common/services/ExamService';

/**
 * MissingSameDayProviderVisitsWidget component
 * Shows all exams missing a same-day provider visit with server-side pagination
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 * @param {boolean} [props.queryEnabled=true] - Whether queries are enabled (stream gate)
 */
export default function MissingSameDayProviderVisitsWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true }) {
const queryClient = useQueryClient();

// Pagination state
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(50);

// Exam pane state
const [showAddExamPane, setShowAddExamPane] = useState(false);
const [selectedPatient, setSelectedPatient] = useState(null);
const [selectedPatientExam, setSelectedPatientExam] = useState(null);
const [examResults, setExamResults] = useState(null);
const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
const [facility, setFacility] = useState(null);

    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: countData, isLoading: isLoadingCount } = useMissingSameDayProviderVisitsCount((isCollapsed && !isExpanded) && queryEnabled);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useMissingSameDayProviderVisitsData(currentPage, pageSize, shouldLoadFullData);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load missing same-day provider visits:', error);
            toast.error(`Failed to load missing same-day provider visits: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        });
    }, []);

    const sortedResults = useMemo(() => {
        if (!results || results.length === 0) return [];
        return [...results].sort((a, b) => new Date(a.examDateTime).getTime() - new Date(b.examDateTime).getTime());
    }, [results]);

    const handleViewExam = useCallback(async (row) => {
        try {
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            if (!row.patientExamID || !row.facilityID) {
                toast.error('Missing required exam information');
                console.error('Missing fields:', { row });
                return;
            }

            const postdata = {
                uniqueID: row.patientExamID,
                facilityID: row.facilityID,
                name: row.examClass,
                userID: userInfo.userId
            };

            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            setSelectedPatient({ dob: data.patientDob });
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({ facilityID: row.facilityID, facilityName: row.facilityName });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    const handleExamPaneClose = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'missing-same-day-provider-visits', currentPage, pageSize] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', 'missing-same-day-provider-visits'] });
        toast.success('Widget data refreshed');
    }, [queryClient, currentPage, pageSize]);

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            ellipsis: true,
            width: 180,
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            width: 140,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewExam(row);
                    }}
                    className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#db2777' : '#be185d';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                    }}
                >
                    View
                    <ExternalLink size={14} />
                </button>
            )
        }
    ], [formatMRN, formatDate, handleViewExam]);

    const getCountColor = () => Globals.theme === 'dark' ? '#ec4899' : '#db2777';

    const getCountMessage = () => count === 1 ? 'missing same-day provider visit' : 'missing same-day provider visits';

    // COLLAPSED STATE � show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </div>
        );
    }

    // Empty state
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p style={{ color: getCountColor() }}>
                    No Missing Same-Day Provider Visits Found
                </p>
            </div>
        );
    }

    // EXPANDED STATE � table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                <Table
                    columns={columns}
                    dataSource={sortedResults}
                    rowKey={(record, index) => `${record.patientName}-${record.examDateTime}-${record.mrn}-${record.examType}-${index}`}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} visits`,
                    }}
                    size="small"
                    scroll={{ y: 500 }}
                    virtual
                    loading={(isLoading && !isExpanded) || isFetching}
                />
            </div>

            {showAddExamPane && selectedPatient && (
                <PatientExamPane
                    patientAge={calculateAge(selectedPatient?.dob)}
                    patientInactive={false}
                    patient={selectedPatient}
                    showPane={showAddExamPane}
                    examInfo={selectedPatientExam}
                    examHistory={examResults}
                    facility={facility}
                    updateExamInfo={handleExamPaneClose}
                    from='missing-same-day-provider-visits'
                    hidePane={() => setShowAddExamPane(false)}
                    sharedFollowUpIDs={sharedFollowUpIDs}
                    getNewExamInfo={handleExamPaneClose}
                />
            )}
        </>
    );
}
